"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

type Review = {
  id: string;
  text: string;
  name: string;
  company?: string;
};

export default function KundenRezensionenCarousel() {
  const reviews: Review[] = useMemo(
    () => [
      {
        id: "stefan",
        text: "Bei uns zählt vor allem, dass es reibungslos läuft. Die Einrichtung ging super schnell und im Alltag ist die Bedienung nach kurzem Einfinden super einfach.",
        name: "Stefan M.",
        company: "Einzelhandel",
      },
      {
        id: "sarah",
        text: "Kartenzahlung, Kassensystem und Auswertungen sind gut aufeinander abgestimmt. Der Support ist jederzeit gut erreichbar und unterstützt zuverlässig bei allen Anliegen.",
        name: "Sarah K.",
        company: "Einzelhandel",
      },
      {
        id: "martina",
        text: "Stabiles System mit guter Performance, auch bei größeren Warenmengen. Exporte funktionieren zuverlässig, insgesamt sehr solide.",
        name: "Martina L.",
        company: "Großhandel",
      },
      {
        id: "jonas",
        text: "Intuitive Bedienung. Schulung war praktisch nicht nötig - das Team konnte direkt loslegen.",
        name: "Jonas B.",
        company: "Dienstleistung",
      },
      {
        id: "daniel",
        text: "Im Alltag leicht zu bedienen. Ein paar Details brauchen etwas Einarbeitung, insgesamt aber eine klare Erleichterung.",
        name: "Daniel R.",
        company: "Dienstleistungsbetrieb",
      },
    ],
    [],
  );

  const slides = useMemo(() => {
    if (reviews.length === 0) return [];
    const first = reviews[0];
    const last = reviews[reviews.length - 1];
    return [last, ...reviews, first];
  }, [reviews]);

  const [idx, setIdx] = useState(1);

  const viewportRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const isTransitioning = useRef(false);

  const pointerDown = useRef(false);
  const startX = useRef(0);
  const deltaX = useRef(0);
  const viewportW = useRef(0);

  const animateToIdx = (targetIdx: number, withTransition = true) => {
    const el = trackRef.current;
    if (!el) return;

    el.style.transition = withTransition
      ? "transform 520ms cubic-bezier(0.22, 1, 0.36, 1)"
      : "none";

    el.style.transform = `translateX(${-targetIdx * 100}%)`;
  };

  useEffect(() => {
    animateToIdx(idx, true);
  }, [idx]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const onEnd = () => {
      const lastIndex = slides.length - 1;

      if (idx === 0) {
        const realLast = reviews.length;
        setIdx(realLast);
        requestAnimationFrame(() => animateToIdx(realLast, false));
      } else if (idx === lastIndex) {
        const realFirst = 1;
        setIdx(realFirst);
        requestAnimationFrame(() => animateToIdx(realFirst, false));
      }

      isTransitioning.current = false;
    };

    el.addEventListener("transitionend", onEnd);
    return () => el.removeEventListener("transitionend", onEnd);
  }, [idx, slides.length, reviews.length]);

  const next = () => {
    if (isTransitioning.current) return;
    isTransitioning.current = true;
    setIdx((i) => i + 1);
  };

  const prev = () => {
    if (isTransitioning.current) return;
    isTransitioning.current = true;
    setIdx((i) => i - 1);
  };

  const activeDot = useMemo(() => {
    const lastIndex = slides.length - 1;
    if (idx === 0) return reviews.length - 1;
    if (idx === lastIndex) return 0;
    return idx - 1;
  }, [idx, reviews.length, slides.length]);

  const arrowBase =
    "w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 shadow-md";

  const onPointerDown = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest("button")) return;

    pointerDown.current = true;
    startX.current = e.clientX;
    deltaX.current = 0;

    viewportW.current = viewportRef.current?.getBoundingClientRect().width || 1;

    if (trackRef.current) trackRef.current.style.transition = "none";

    viewportRef.current?.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointerDown.current) return;

    deltaX.current = e.clientX - startX.current;

    if (trackRef.current) {
      trackRef.current.style.transform = `translateX(calc(${-idx * 100}% + ${deltaX.current}px))`;
    }
  };

  const finishDrag = () => {
    if (!pointerDown.current) return;
    pointerDown.current = false;

    const threshold = Math.min(80, viewportW.current * 0.18);

    if (deltaX.current > threshold) prev();
    else if (deltaX.current < -threshold) next();
    else animateToIdx(idx, true);

    deltaX.current = 0;
  };

  if (slides.length === 0) return null;

  return (
    <div className="max-w-6xl mx-auto">
      <div
        ref={viewportRef}
        className="relative"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
      >
        {/* Pfeile - Brand-Farben */}
        <button
          type="button"
          onClick={prev}
          onPointerDown={(e) => e.stopPropagation()}
          className={`
            ${arrowBase}
            absolute left-0 top-1/2 -translate-y-1/2 -translate-x-[140%] z-10
            bg-brand-primary
            hover:bg-brand-primary-dark hover:-translate-y-1 hover:shadow-xl
          `}
          aria-label="Vorherige Rezension"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>

        <button
          type="button"
          onClick={next}
          onPointerDown={(e) => e.stopPropagation()}
          className={`
            ${arrowBase}
            absolute right-0 top-1/2 -translate-y-1/2 translate-x-[140%] z-10
            bg-brand-primary
            hover:bg-brand-primary-dark hover:-translate-y-1 hover:shadow-xl
          `}
          aria-label="Nächste Rezension"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </button>

        {/* Panel */}
        <div
          className="
        bg-white dark:bg-white
          rounded-2xl
          px-10 sm:px-14
          py-16
          min-h-85
          overflow-hidden"
        >
          {/* Track */}
          <div ref={trackRef} className="flex will-change-transform">
            {slides.map((r, i) => (
              <div
                key={`${r.id}-${i}`}
                className="min-w-full flex justify-center"
              >
                <div className="max-w-180 w-full">
                  <p className="text-[24px] leading-[1.6] text-slate-800">
                    "{r.text}"
                  </p>

                  <div className="mt-12 text-left">
                    <p className="text-base font-semibold text-slate-700">
                      {r.name}
                    </p>
                    {r.company && (
                      <p className="text-sm text-slate-500">
                        {r.company}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dots - Brand-Farben */}
          <div className="mt-10 flex justify-center gap-8">
            {reviews.map((r, i) => (
              <button
                key={r.id}
                type="button"
                onClick={() => {
                  if (isTransitioning.current) return;
                  isTransitioning.current = true;
                  setIdx(i + 1);
                }}
                onPointerDown={(e) => e.stopPropagation()}
                className={`
                  w-3 h-3 rounded-full transition-all duration-300
                  ${
                    i === activeDot
                      ? "bg-brand-primary scale-125"
                      : "bg-gray-300 dark:bg-slate-600 hover:bg-brand-primary-dark hover:-translate-y-1 hover:shadow-xl hover:scale-110"
                  }
                `}
                aria-label={`Rezension ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
